import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faBrain,
  faChartLine,
  faCheckCircle,
  faCalendarCheck,
  faBolt,
  faWandMagicSparkles,
  faPaintbrush,
  faSync,
  faDatabase,
  faStethoscope,
  faUserNurse,
  faHospital,
  faHeartPulse,
  faTooth,
  faCapsules,
  faFileMedical,
  faSyringe,
  faNotesMedical
} from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../../components/ui/BtnTaratic';

const LandingPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex-1 flex flex-col relative overflow-hidden">
      {/* Floating Medical Background Icons */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-0">
        <FontAwesomeIcon icon={faStethoscope} className="absolute top-[8%] left-[5%] text-blue-500/10 text-6xl transform -rotate-12 animate-pulse" />
        <FontAwesomeIcon icon={faHeartPulse} className="absolute top-[18%] right-[8%] text-indigo-500/10 text-8xl transform rotate-12" />
        <FontAwesomeIcon icon={faTooth} className="absolute top-[35%] left-[10%] text-sky-500/15 text-5xl transform rotate-45" />
        <FontAwesomeIcon icon={faHospital} className="absolute top-[48%] right-[5%] text-blue-600/10 text-9xl transform -rotate-6" />
        <FontAwesomeIcon icon={faUserNurse} className="absolute top-[65%] left-[6%] text-purple-500/10 text-7xl transform rotate-12" />
        <FontAwesomeIcon icon={faCapsules} className="absolute top-[75%] right-[12%] text-teal-500/15 text-6xl transform -rotate-45" />
        <FontAwesomeIcon icon={faFileMedical} className="absolute top-[25%] left-[48%] text-blue-400/5 text-9xl transform -rotate-12" />
        <FontAwesomeIcon icon={faSyringe} className="absolute top-[88%] left-[18%] text-indigo-400/10 text-5xl transform rotate-12" />
        <FontAwesomeIcon icon={faNotesMedical} className="absolute top-[85%] right-[25%] text-blue-500/10 text-7xl transform -rotate-12" />
        <FontAwesomeIcon icon={faStethoscope} className="absolute top-[55%] right-[45%] text-sky-500/5 text-8xl transform rotate-45" />
        <FontAwesomeIcon icon={faHeartPulse} className="absolute top-[92%] left-[45%] text-purple-500/10 text-6xl transform -rotate-6" />
        <FontAwesomeIcon icon={faTooth} className="absolute top-[12%] left-[30%] text-blue-500/10 text-4xl transform -rotate-12" />
      </div>

      {/* Hero Section */}
      <section className="flex-1 flex flex-col items-center justify-center text-center px-6 py-20 relative z-10">
        <div className="max-w-4xl mx-auto space-y-8 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200/80 text-blue-600 text-xs font-semibold tracking-wider uppercase mb-2 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            {t('landing.badgeTag', 'IA especializada en Clínicas')}
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-gray-900 tracking-tight leading-[1.15]">
            {t('landing.titlePrefix', 'De Instagram a tu ERP:')} <br className="hidden md:block" />
            <span className="text-blue-600 font-extrabold">
              {t('landing.titleAuto', 'Citas agendadas')}{' '}
              <span className="relative inline-block pb-1.5">
                {t('landing.titleAutoHighlight', 'automáticamente')}
                <span className="absolute left-0 right-0 bottom-0 h-[5px] rounded-full bg-blue-600 shadow-[0_3px_6px_rgba(37,99,235,0.25)] pointer-events-none" />
              </span>
            </span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed font-normal">
            {t('landing.subtitlePart1', 'Atención 24/7 a tus pacientes, ')}
            <strong className="font-bold text-gray-800">
              {t('landing.subtitleHighlight', 'IA única dedicada para cada paciente')}
            </strong>
            {t('landing.subtitlePart2', ' y sinergia operativa con tu tipo de clínica.')}
          </p>

          <div className="flex flex-col items-center gap-6 pt-4">
            <Link to="/request-access">
              <BtnTaratic color="blue" className="px-9 py-2 text-xl font-bold shadow-xl shadow-blue-500/20 hover:shadow-blue-500/40 transition-all">
                {t('landing.ctaAccess', 'Solicitar Acceso')}
              </BtnTaratic>
            </Link>
          </div>
        </div>

        {/* Ultra-Modern Hero Cards Container */}
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 mt-24 mb-16 z-10 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 items-stretch">

            {/* Card 1: Atención 24/7 */}
            <div className="group relative bg-white/90 backdrop-blur-xl rounded-[2.5rem] p-8 transition-all duration-500 hover:-translate-y-2 shadow-[0_15px_40px_rgba(37,99,235,0.15),_0_0_20px_rgba(37,99,235,0.1)] hover:shadow-[0_25px_60px_rgba(37,99,235,0.25)] flex flex-col justify-between overflow-hidden text-left border-0 outline-none">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-400/20 to-transparent rounded-bl-[120px] pointer-events-none opacity-80" />

              <div>
                {/* Header Badge & Number */}
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 border-none text-blue-600 flex items-center justify-center shadow-sm">
                    <FontAwesomeIcon icon={faClock} className="text-xl" />
                  </div>
                  <span className="text-xs font-mono font-semibold px-3 py-1 rounded-full bg-gray-100/80 text-gray-500 border-none">
                    {t('landing.card1.badge', '01 / 24/7')}
                  </span>
                </div>

                <h3 className="text-2xl font-extrabold text-gray-900 mb-3 tracking-tight group-hover:text-blue-600 transition-colors">
                  {t('landing.card1.title', 'Atención 24/7')}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed mb-6 font-normal">
                  {t('landing.card1.desc', 'No pierdas más clientes por no responder a tiempo. Nuestro bot atiende todas las consultas al instante.')}
                </p>
              </div>

              {/* Embedded Live Chat UI Widget with Inner Glow */}
              <div className="bg-gray-50/90 rounded-2xl p-4 space-y-3 mt-4 group-hover:bg-blue-50/40 transition-colors shadow-[inset_0_0_25px_rgba(0,0,0,0.03)] border-none">
                <div className="flex items-center justify-between text-[11px] font-semibold text-gray-500 border-b border-gray-200/50 pb-2">
                  <span className="flex items-center gap-1.5 text-blue-600">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                    {t('landing.card1.responseLabel', 'Respuesta Inmediata')}
                  </span>
                  <span className="text-gray-400 font-mono">{t('landing.card1.responseTime', '0.4s')}</span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="bg-white border-none p-2.5 rounded-xl text-gray-600 max-w-[85%] shadow-sm">
                    {t('landing.card1.userMsg', '¿Tenéis disponibilidad mañana a las 10:00h?')}
                  </div>
                  <div className="bg-blue-600 text-white p-2.5 rounded-xl ml-auto max-w-[88%] shadow-sm font-medium flex items-start gap-2 border-none">
                    <FontAwesomeIcon icon={faCalendarCheck} className="text-blue-200 mt-0.5 shrink-0" />
                    <span>{t('landing.card1.botMsg1', 'Hola María, ¿querías otra limpieza dental?')}</span>
                  </div>
                  <div className="bg-blue-600 text-white p-2.5 rounded-xl ml-auto max-w-[88%] shadow-sm font-medium flex items-start gap-2 border-none">
                    <FontAwesomeIcon icon={faCalendarCheck} className="text-blue-200 mt-0.5 shrink-0" />
                    <span>{t('landing.card1.botMsg2', 'Si es así, tenemos un hueco disponible a las 10:00h. ¿Te agendo la cita?')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Respuestas Inteligentes */}
            <div className="group relative bg-white/90 backdrop-blur-xl rounded-[2.5rem] p-8 transition-all duration-500 hover:-translate-y-2 shadow-[0_15px_40px_rgba(147,51,234,0.15),_0_0_20px_rgba(147,51,234,0.1)] hover:shadow-[0_25px_60px_rgba(147,51,234,0.25)] flex flex-col justify-between overflow-hidden text-left md:translate-y-4 border-0 outline-none">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-400/20 to-transparent rounded-bl-[120px] pointer-events-none opacity-80" />

              <div>
                {/* Header Badge & Number */}
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-purple-50 border-none text-purple-600 flex items-center justify-center shadow-sm">
                    <FontAwesomeIcon icon={faBrain} className="text-xl" />
                  </div>
                  <span className="text-xs font-mono font-semibold px-3 py-1 rounded-full bg-gray-100/80 text-gray-500 border-none">
                    {t('landing.card2.badge', '02 / SMART')}
                  </span>
                </div>

                <h3 className="text-2xl font-extrabold text-gray-900 mb-3 tracking-tight group-hover:text-purple-600 transition-colors">
                  {t('landing.card2.title', 'Respuestas Inteligentes')}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed mb-6 font-normal">
                  {t('landing.card2.desc', 'Entiende el contexto y responde de forma natural, guiando al paciente hacia la reserva de su cita.')}
                </p>
              </div>

              {/* Embedded AI Intelligence UI Widget with Inner Glow */}
              <div className="bg-gray-50/90 rounded-2xl p-4 space-y-3 mt-4 group-hover:bg-purple-50/40 transition-colors shadow-[inset_0_0_25px_rgba(0,0,0,0.03)] border-none">
                <div className="flex items-center justify-between text-[11px] font-semibold text-gray-500 border-b border-gray-200/50 pb-2">
                  <span className="flex items-center gap-1 text-purple-600">
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="text-xs" />
                    {t('landing.card2.nlpLabel', 'Precisión en Contexto Clínico')}
                  </span>
                  <span className="bg-purple-100 text-purple-700 text-[10px] px-2 py-0.5 rounded-full font-bold">{t('landing.card2.nlpValue', '99.8%')}</span>
                </div>

                <div className="flex flex-col gap-2 text-[11px]">
                  <div className="bg-white border-none p-2 rounded-xl text-gray-700 font-medium flex items-center gap-1.5 shadow-sm">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-purple-500 shrink-0" />
                    <span>{t('landing.card2.intentDetected', 'Intención Detectada')}</span>
                  </div>
                  <div className="bg-white border-none p-2 rounded-xl text-gray-700 font-medium flex items-center gap-1.5 shadow-sm">
                    <FontAwesomeIcon icon={faBolt} className="text-purple-500 shrink-0" />
                    <span>{t('landing.card2.customTone', 'Recuperación de pacientes y confirmación de cita')}</span>
                  </div>
                  <div className="bg-white border-none p-2 rounded-xl text-gray-700 font-medium flex items-center gap-1.5 shadow-sm">
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="text-amber-500 shrink-0" />
                    <span>{t('landing.card2.uniqueResponse', 'Respuesta única a cada paciente')}</span>
                  </div>
                  <div className="bg-white border-none p-2 rounded-xl text-gray-700 font-medium flex items-center gap-1.5 shadow-sm">
                    <FontAwesomeIcon icon={faUserNurse} className="text-purple-500 shrink-0" />
                    <span>{t('landing.card2.patientInfoUse', 'Usa la información del paciente para responder')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Más Pacientes */}
            <div className="group relative bg-white/90 backdrop-blur-xl rounded-[2.5rem] p-8 transition-all duration-500 hover:-translate-y-2 shadow-[0_15px_40px_rgba(16,185,129,0.15),_0_0_20px_rgba(16,185,129,0.1)] hover:shadow-[0_25px_60px_rgba(16,185,129,0.25)] flex flex-col justify-between overflow-hidden text-left md:translate-y-8 border-0 outline-none">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-400/20 to-transparent rounded-bl-[120px] pointer-events-none opacity-80" />

              <div>
                {/* Header Badge & Number */}
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 border-none text-emerald-600 flex items-center justify-center shadow-sm">
                    <FontAwesomeIcon icon={faChartLine} className="text-xl" />
                  </div>
                  <span className="text-xs font-mono font-semibold px-3 py-1 rounded-full bg-gray-100/80 text-gray-500 border-none">
                    {t('landing.card3.badge', '03 / METRICS')}
                  </span>
                </div>

                <h3 className="text-2xl font-extrabold text-gray-900 mb-3 tracking-tight group-hover:text-emerald-600 transition-colors">
                  {t('landing.card3.title', 'Más Pacientes')}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed mb-6 font-normal">
                  {t('landing.card3.desc', 'Analiza las estadísticas y optimiza tus procesos para convertir más seguidores en pacientes reales.')}
                </p>
              </div>

              {/* Embedded Metrics Growth UI Widget with Inner Glow */}
              <div className="bg-gray-50/90 rounded-2xl p-4 space-y-3 mt-4 group-hover:bg-emerald-50/40 transition-colors shadow-[inset_0_0_25px_rgba(0,0,0,0.03)] border-none">
                <div className="flex items-center justify-between text-[11px] font-semibold text-gray-500 border-b border-gray-200/50 pb-2">
                  <span className="text-emerald-600">{t('landing.card3.conversionRateLabel', 'Tasa de Conversión')}</span>
                  <span className="text-emerald-600 font-bold text-xs flex items-center gap-1">
                    {t('landing.card3.conversionRateValue', '↑ +142%')}
                  </span>
                </div>

                {/* Simulated Mini Metric Progress Bars */}
                <div className="space-y-2.5 text-[10px] font-medium text-gray-600">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span>{t('landing.card3.metricLabel', 'Mensajes → Citas Reservadas')}</span>
                      <span className="font-bold text-gray-900">{t('landing.card3.metricValue', '84%')}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-emerald-500 to-teal-400 h-full rounded-full w-[84%]" />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span>{t('landing.card3.metricHighValueLabel', 'Nuevos Pacientes de Alto Valor')}</span>
                      <span className="font-bold text-emerald-600">{t('landing.card3.metricHighValue', '+68%')}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-emerald-600 to-teal-500 h-full rounded-full w-[68%]" />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span>{t('landing.card3.metricAttendanceLabel', 'Tasa de Asistencia a Citas')}</span>
                      <span className="font-bold text-teal-600">{t('landing.card3.metricAttendance', '96%')}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-teal-500 to-emerald-400 h-full rounded-full w-[96%]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* ERP Integration Highlights (Moved below cards) */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs text-gray-600 font-medium max-w-2xl mx-auto">
            <span className="flex items-center gap-2.5 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl border border-gray-200/80 shadow-md shadow-blue-500/5 font-bold text-gray-800 transition-all hover:scale-105">
              <FontAwesomeIcon icon={faPaintbrush} className="text-blue-600 text-base" />
              {t('landing.erpConnection', 'Personaliza las salas, horarios y trabajadores')}
            </span>
            <span className="flex items-center gap-2.5 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl border border-gray-200/80 shadow-md shadow-indigo-500/5 font-bold text-gray-800 transition-all hover:scale-105">
              <FontAwesomeIcon icon={faSync} className="text-indigo-600 text-base" />
              {t('landing.realtimeCalendar', 'Agenda en tiempo real')}
            </span>
            <span className="flex items-center gap-2.5 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl border border-gray-200/80 shadow-md shadow-emerald-500/5 font-bold text-gray-800 transition-all hover:scale-105">
              <FontAwesomeIcon icon={faDatabase} className="text-emerald-600 text-base" />
              {t('landing.unifiedHistory', 'Historial unificado')}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
